import { useRef, useState, type ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Building2, Crown } from "lucide-react";
import {
  AlertBanner,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Heading,
  Icon,
  SelectField,
  Skeleton,
  Text,
} from "@lendcord/concord";
import { getInitials } from "@/app/initials";
import { getFullName } from "@/app/profile/fullName";
import { MAX_IMAGE_UPLOAD_SIZE_BYTES } from "@/app/profile/documentUploadLimits";
import { compressImageToLimit } from "@/app/profile/imageCompression";
import { useSession } from "@/app/session";
import { getRoleFromToken } from "@/app/tokenStorage";
import {
  useDeleteAccountMutation,
  useListAvailableEnterprisesQuery,
  useRequestJoinEnterpriseMutation,
  useRequestRoleUpgradeMutation,
} from "@/app/profile/api/profile.api";
import { isQueryApiError } from "@/app/store/baseApi";
import {
  useConfirmDocumentUploadMutation,
  useGetDocumentDownloadUrlQuery,
  useRequestDocumentUploadUrlMutation,
  uploadDocumentFile,
} from "@/features/profile/api/profile.api";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { UpgradeRequestStatusBanner } from "@/features/profile/components/UpgradeRequestStatusBanner";
import { JoinEnterpriseStatusBanner } from "@/features/profile/components/JoinEnterpriseStatusBanner";
import type { Profile, ProfileDocument } from "@/app/profile/profile.types";

interface ProfileIdentityCardProps {
  profile: Profile | null;
  documents: ProfileDocument[] | null;
  refetch: () => unknown;
}

export function ProfileIdentityCard({ profile, documents, refetch }: ProfileIdentityCardProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { logout } = useSession();
  const fullName = getFullName(profile);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const [requestDocumentUploadUrl] = useRequestDocumentUploadUrlMutation();
  const [confirmDocumentUpload] = useConfirmDocumentUploadMutation();
  const [deleteAccount, { isLoading: isDeletingAccount }] = useDeleteAccountMutation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const [isUpgradeConfirmOpen, setIsUpgradeConfirmOpen] = useState(false);
  const [requestRoleUpgrade, { isLoading: isRequestingUpgrade }] = useRequestRoleUpgradeMutation();
  const [upgradeError, setUpgradeError] = useState<string | undefined>();
  const [isJoinEnterpriseOpen, setIsJoinEnterpriseOpen] = useState(false);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState("");
  const [requestJoinEnterprise, { isLoading: isRequestingJoin }] = useRequestJoinEnterpriseMutation();
  const [joinError, setJoinError] = useState<string | undefined>();
  // Platform hierarchy: ADMIN -> ENTERPRISE (creditor) -> {STAFF, AGENT,
  // BORROWER, LENDER} — only a LENDER can request the creditor/enterprise
  // upgrade, so this reads the JWT's own role claim rather than assuming
  // "the only role this app builds today" the way the rest of apps/app does.
  const isLender = getRoleFromToken() === "LENDER";
  // Base eligibility for join-enterprise is BORROWER or LENDER (see
  // lendcord-core's JoinEnterpriseRequestService.isEligibleBase) — reading
  // the role here too so the picker never even mounts for a role it can
  // never apply as, same reasoning as isLender above.
  const role = getRoleFromToken();
  const isJoinEnterpriseEligibleRole = role === "BORROWER" || role === "LENDER";

  const upgradeRequest = profile?.roleUpgradeRequest;
  const canUpgrade = upgradeRequest?.allowRequestForUpgradation ?? true;

  const joinEnterpriseRequest = profile?.joinEnterpriseRequest;
  const canJoinEnterprise = joinEnterpriseRequest?.allowJoinEnterprise ?? true;
  const { data: availableEnterprises, isLoading: isLoadingEnterprises } = useListAvailableEnterprisesQuery(
    undefined,
    { skip: !isJoinEnterpriseEligibleRole },
  );

  async function handleJoinEnterprise() {
    if (!selectedEnterpriseId) return;
    setJoinError(undefined);
    try {
      await requestJoinEnterprise({ enterpriseId: selectedEnterpriseId }).unwrap();
      refetch();
      setIsJoinEnterpriseOpen(false);
      setSelectedEnterpriseId("");
    } catch (error) {
      setJoinError(isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.joinEnterpriseError" }));
    }
  }

  async function handleUpgradeToCreditor() {
    setUpgradeError(undefined);
    try {
      await requestRoleUpgrade().unwrap();
      refetch();
      setIsUpgradeConfirmOpen(false);
    } catch (error) {
      setUpgradeError(isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.upgradeError" }));
    }
  }

  async function handleDeleteAccount() {
    setDeleteError(undefined);
    try {
      await deleteAccount().unwrap();
      logout();
      navigate("/auth", { replace: true });
    } catch (error) {
      setDeleteError(
        isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.profile.deleteAccountError" }),
      );
    }
  }

  const photoDocument = documents?.find((d) => d.type === "PHOTO" && d.status !== "REJECTED");
  const { data: photoUrlData } = useGetDocumentDownloadUrlQuery(photoDocument ? photoDocument.id : skipToken);
  const photoUrl = photoUrlData?.url ?? null;

  async function handleAvatarClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    let file = event.target.files?.[0];
    event.target.value = "";
    if (!file || isUploading) return;

    setUploadError(undefined);

    if (file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
      file = await compressImageToLimit(file, MAX_IMAGE_UPLOAD_SIZE_BYTES);
    }
    if (file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
      setUploadError(intl.formatMessage({ id: "lender.profile.documentUploadTooLarge" }));
      return;
    }

    setIsUploading(true);
    try {
      const { storageKey, uploadUrl } = await requestDocumentUploadUrl("PHOTO").unwrap();
      await uploadDocumentFile(uploadUrl, file);
      await confirmDocumentUpload({ type: "PHOTO", storageKey }).unwrap();
      await refetch();
    } catch {
      setUploadError(intl.formatMessage({ id: "lender.profile.documentUploadError" }));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="rounded-md border border-border bg-surface-raised p-6 shadow-sm">
      <div className="flex flex-col items-center text-center">
        {profile ? (
          <button
            type="button"
            onClick={handleAvatarClick}
            disabled={isUploading}
            className="group relative cursor-pointer"
            aria-label={intl.formatMessage({ id: "lender.profile.uploadPhotoLabel" })}
          >
            <Avatar size="lg" className="h-20 w-20 bg-accent-600">
              {photoUrl && <AvatarImage src={photoUrl} alt={fullName ?? ""} />}
              <AvatarFallback className="text-xl font-bold text-white">
                {fullName ? getInitials(fullName) : "–"}
              </AvatarFallback>
            </Avatar>
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
              <Text size="xs" className="font-medium text-white">
                <FormattedMessage id="lender.profile.editPhotoLabel" />
              </Text>
            </span>
          </button>
        ) : (
          <Skeleton variant="circle" className="h-20 w-20" />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelected}
          className="hidden"
        />

        {profile ? (
          <Heading level={3} className="mt-4">
            {fullName ?? "–"}
          </Heading>
        ) : (
          <Skeleton variant="text" className="mt-4 h-7 w-40" />
        )}
      </div>

      {uploadError && (
        <AlertBanner status="error" className="mt-4">
          {uploadError}
        </AlertBanner>
      )}

      <div className="mt-6 flex flex-col gap-4">
        <div>
          <Text size="xs" variant="muted">
            <FormattedMessage id="lender.profile.lenderIdLabel" />
          </Text>
          {profile ? (
            <Text size="sm" className="font-medium">
              {profile.readableId ?? "–"}
            </Text>
          ) : (
            <Skeleton variant="text" className="mt-0.5 h-5 w-24" />
          )}
        </div>

        <div className="flex items-start justify-between gap-3">
          <ProfileField labelId="lender.profile.panLabel" value={profile?.pan ?? "–"} loading={!profile} />
          {profile?.panVerified && (
            <Badge status="success">
              <FormattedMessage id="lender.profile.verifiedBadge" />
            </Badge>
          )}
        </div>

        <div className="flex items-start justify-between gap-3">
          <ProfileField labelId="lender.profile.phoneLabel" value={profile?.phone ?? "–"} loading={!profile} />
          {profile?.phoneVerified && (
            <Badge status="success">
              <FormattedMessage id="lender.profile.verifiedBadge" />
            </Badge>
          )}
        </div>

        <div className="flex items-start justify-between gap-3">
          <ProfileField labelId="lender.profile.emailLabel" value={profile?.email ?? "–"} loading={!profile} />
          {profile?.emailVerified && (
            <Badge status="success">
              <FormattedMessage id="lender.profile.verifiedBadge" />
            </Badge>
          )}
        </div>
      </div>

      {isLender && (
        <>
          {canUpgrade ? (
            <>
              {/* Solid gold fill, not the accent color — this is a premium/upsell
                  CTA, not a primary navigation action, so it intentionally
                  stands apart from every other primary button on this page.
                  Reuses the `warning` semantic token's solid/on-solid pair
                  rather than a new hardcoded color: that ramp is already a
                  muted gold/amber (#B48A2E light / same in dark, see
                  tokens/colors.ts's lightSemantics/darkSemantics) — "light
                  golden" without inventing an unaudited one-off hex value or a
                  token whose name (`warning`) doesn't match this button's
                  actual meaning. See docs/design-system.md's "Upgrade to
                  Creditor button" note for the full rationale. */}
              <Button
                type="button"
                onClick={() => setIsUpgradeConfirmOpen(true)}
                className="mt-8 w-full bg-warning-solid text-warning-on-solid hover:opacity-90"
              >
                <Icon icon={Crown} size="sm" />
                <FormattedMessage id="lender.profile.upgradeToCreditorButton" />
              </Button>

              <Dialog open={isUpgradeConfirmOpen} onOpenChange={setIsUpgradeConfirmOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      <FormattedMessage id="lender.profile.upgradeToCreditorConfirmHeading" />
                    </DialogTitle>
                    <DialogDescription>
                      <FormattedMessage id="lender.profile.upgradeToCreditorConfirmDescription" />
                    </DialogDescription>
                  </DialogHeader>

                  {upgradeError && <AlertBanner status="error">{upgradeError}</AlertBanner>}

                  <DialogFooter>
                    <Button type="button" variant="secondary" onClick={() => setIsUpgradeConfirmOpen(false)}>
                      <FormattedMessage id="lender.profile.upgradeToCreditorCancelButton" />
                    </Button>
                    <Button type="button" onClick={handleUpgradeToCreditor} disabled={isRequestingUpgrade}>
                      {isRequestingUpgrade ? (
                        <FormattedMessage id="lender.profile.upgradeToCreditorRequestingButton" />
                      ) : (
                        <FormattedMessage id="lender.profile.upgradeToCreditorConfirmButton" />
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <UpgradeRequestStatusBanner status={upgradeRequest?.status} remarks={upgradeRequest?.remarks ?? undefined} />
          )}
        </>
      )}

      {isJoinEnterpriseEligibleRole && (
        <>
          {canJoinEnterprise ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsJoinEnterpriseOpen(true)}
                className="mt-3 w-full"
              >
                <Icon icon={Building2} size="sm" />
                <FormattedMessage id="lender.profile.joinEnterpriseButton" />
              </Button>

              <Dialog
                open={isJoinEnterpriseOpen}
                onOpenChange={(open) => {
                  setIsJoinEnterpriseOpen(open);
                  if (!open) {
                    setSelectedEnterpriseId("");
                    setJoinError(undefined);
                  }
                }}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      <FormattedMessage id="lender.profile.joinEnterpriseDialogTitle" />
                    </DialogTitle>
                    <DialogDescription>
                      <FormattedMessage id="lender.profile.joinEnterpriseDialogDescription" />
                    </DialogDescription>
                  </DialogHeader>

                  {joinError && <AlertBanner status="error">{joinError}</AlertBanner>}

                  <SelectField
                    label={intl.formatMessage({ id: "lender.profile.joinEnterprisePickerLabel" })}
                    placeholder={intl.formatMessage({
                      id: isLoadingEnterprises
                        ? "lender.profile.joinEnterpriseLoadingPlaceholder"
                        : "lender.profile.joinEnterprisePickerPlaceholder",
                    })}
                    value={selectedEnterpriseId}
                    onValueChange={setSelectedEnterpriseId}
                    disabled={isLoadingEnterprises || (availableEnterprises?.length ?? 0) === 0}
                    options={(availableEnterprises ?? []).map((enterprise) => ({
                      value: enterprise.id,
                      label: `${enterprise.name} (${enterprise.readableId})`,
                    }))}
                  />
                  {!isLoadingEnterprises && availableEnterprises?.length === 0 && (
                    <Text size="xs" variant="muted" className="mt-1">
                      <FormattedMessage id="lender.profile.joinEnterpriseNoneAvailable" />
                    </Text>
                  )}

                  <DialogFooter>
                    <Button type="button" variant="secondary" onClick={() => setIsJoinEnterpriseOpen(false)}>
                      <FormattedMessage id="lender.shared.cancelButton" />
                    </Button>
                    <Button
                      type="button"
                      onClick={handleJoinEnterprise}
                      disabled={isRequestingJoin || !selectedEnterpriseId}
                    >
                      {isRequestingJoin ? (
                        <FormattedMessage id="lender.profile.joinEnterpriseSendingButton" />
                      ) : (
                        <FormattedMessage id="lender.profile.joinEnterpriseSendButton" />
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <JoinEnterpriseStatusBanner
              status={joinEnterpriseRequest?.status}
              enterpriseName={joinEnterpriseRequest?.enterpriseName}
              remarks={joinEnterpriseRequest?.remarks}
            />
          )}
        </>
      )}

      <Button
        type="button"
        variant="secondary"
        onClick={() => setIsDeleteConfirmOpen(true)}
        className="mt-3 w-full border-error-border text-error-fg hover:bg-error-bg"
      >
        <FormattedMessage id="lender.profile.deleteAccountButton" />
      </Button>

      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="lender.profile.deleteAccountConfirmHeading" />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage id="lender.profile.deleteAccountConfirmDescription" />
            </DialogDescription>
          </DialogHeader>

          {deleteError && <AlertBanner status="error">{deleteError}</AlertBanner>}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsDeleteConfirmOpen(false)}
              disabled={isDeletingAccount}
            >
              <FormattedMessage id="lender.profile.deleteAccountCancelButton" />
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteAccount} disabled={isDeletingAccount}>
              {isDeletingAccount ? (
                <FormattedMessage id="lender.profile.deletingAccountButton" />
              ) : (
                <FormattedMessage id="lender.profile.deleteAccountButton" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
